import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Paper,
  Button,
  Snackbar,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { useHistory, useLocation } from "react-router-dom";
import useDebouncedSessionStorage from "./useDebouncedSessionStorage";

import ProductDetails from "./steps/ProductDetails";
import StatusSelection from "./steps/StatusSelection";
import DocumentDeliverables from "./steps/DocumentDeliverables";
import AddCampaignID from "./steps/AddCampaignID";
import ReviewAndAddProject from "./steps/ReviewAndAddProject";

import {
  fetchFGStatusesAPI,
  saveStatusSelectionAPI,
  saveCampaignIDAPI,
  submitProductDevelopmentAPI,
  fetchProjectDetailsAPI,
} from "./stepperAPI";
import { useStyles } from './ProductDevelopmentStepperStyles';

const steps = [
  "Project Details",
  "Status Selection",
  "Document Deliverables",
  "Add Campaign ID",
  "Review And Add Project",
];

const STORAGE_KEYS = {
  FORM_DATA: "productDevelopmentFormData",
  CURRENT_STEP: "productDevelopmentCurrentStep",
  COMPLETED_STEPS: "productDevelopmentCompletedSteps",
};

const ProductDevelopmentStepper = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const isMountedRef = useRef(true);

  const isEditMode = location?.state?.editProject || false;
  const projectData = location?.state?.projectData || null;
  const isEditCampaign = location?.state?.editCampaign || false;

  const [formData, setFormData] = useState(() => {
    try {
      // For edit mode, do NOT hydrate from sessionStorage – always start from API
      if (isEditMode || isEditCampaign) {
        return {};
      }
      // For new projects, load from sessionStorage if available
      const saved = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Error reading from sessionStorage:", error);
      return {};
    }
  });
  
  const [loadingProjectData, setLoadingProjectData] = useState(false);

  const [activeStep, setActiveStep] = useState(() => {
    try {
      if (isEditMode || isEditCampaign) {
        return 4;
      }
      // Check if there's already a saved step in sessionStorage
      const saved = sessionStorage.getItem(STORAGE_KEYS.CURRENT_STEP);
      if (saved !== null) {
        const stepIndex = parseInt(saved, 10);
        if (!isNaN(stepIndex) && stepIndex >= 0 && stepIndex < steps.length) {
          return stepIndex;
        }
      }
      return 0;
    } catch {
      return (isEditMode || isEditCampaign) ? 4 : 0;
    }
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [skipReplicateDialog, setSkipReplicateDialog] = useState(false);
  const [cancelWarningDialogOpen, setCancelWarningDialogOpen] = useState(false);

  const { debouncedSetItem, flush } = useDebouncedSessionStorage(
    STORAGE_KEYS.FORM_DATA,
    500
  );

  useEffect(() => {
    isMountedRef.current = true;

    // In edit mode, clear any existing session cache so only API data is shown
    if (isEditMode || isEditCampaign) {
      try {
        sessionStorage.removeItem(STORAGE_KEYS.FORM_DATA);
        sessionStorage.removeItem(STORAGE_KEYS.CURRENT_STEP);
        sessionStorage.removeItem(STORAGE_KEYS.COMPLETED_STEPS);
      } catch (e) {
        console.warn("Unable to clear product development session storage in edit mode:", e);
      }
    }

    setIsInitialized(true);
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch project details when in edit mode - call API when Edit Project is clicked
  useEffect(() => {
    const fetchProjectDetails = async () => {
      // Get collabrixProjectID from location state or projectData
      // The project object from dashboard might have different field names
      const collabrixProjectID = projectData?.collabrixProjectID || 
                                 projectData?.collabrixProjectID ||
                                 location?.state?.collabrixProjectID ||
                                 projectData?.id; // Fallback to id if collabrixProjectID not available
      
      if ((isEditMode || isEditCampaign)) {
        if (!collabrixProjectID) {
          console.error("❌ collabrixProjectID is required for edit mode. projectData:", projectData);
          setLoadingProjectData(false);
          return;
        }
        
        try {
          setLoadingProjectData(true);
          console.log("📡 Fetching project details for edit mode - calling API:", collabrixProjectID);
          console.log("📡 projectData received:", projectData);
          const response = await fetchProjectDetailsAPI(collabrixProjectID);
          
          console.log("📡 API Response:", response);
          
          if (response?.status && response?.data) {
            const apiData = response.data;
            const projectDetails = apiData.projectDetails || {};
            
            console.log("📡 Mapped projectDetails:", projectDetails);
            console.log("📡 API Data structure:", apiData);
            
            // Map API response to formData structure
            const mappedData = {
              productDetails: {
                collabrixProductID: projectDetails.collabrixProductID || "",
                collabrixProjectID: projectDetails.collabrixProjectID || collabrixProjectID,
                selectedProduct: projectDetails.collabrixProductID || "",
                // cmoSite should be the cdmoOrgID (vendorMosymphonyID) as string for matching with dropdown
                cmoSite: projectDetails.cdmoOrgList?.[0]?.cdmoOrgID?.toString() || "",
                projectID: projectDetails.projectName || "",
                deliverableType: (projectDetails.deliverableTypes || []).map(dt => dt.deliverableTypeName || dt.deliverableTypeID),
                deliverableTypeIDs: (projectDetails.deliverableTypes || []).map(dt => dt.deliverableTypeID).join(","),
                projectStartDate: projectDetails.projectStartDate || null,
                projectEndDate: projectDetails.projectEndDate || null,
                sponsorOrgID: projectDetails.sponsorOrgID || "",
                cdmoOrgID: projectDetails.cdmoOrgList?.[0]?.cdmoOrgID || "",
              },
              fgStatuses: {
                projectStatuses: (apiData.projectStatuses || []).map(s => ({
                  status_id: s.statusID,
                  status_name: s.statusName,
                  activated: s.sponsorSelection || s.cmoSelection || false,
                })),
                campaignStatuses: (apiData.campaignStatuses || []).map(s => ({
                  status_id: s.statusID,
                  status_name: s.statusName,
                  activated: s.sponsorSelection || s.cmoSelection || false,
                })),
                changeControlStatuses: (apiData.changeControlStatuses || []).map(s => ({
                  status_id: s.statusID,
                  status_name: s.statusName,
                  activated: s.sponsorSelection || s.cmoSelection || false,
                })),
                deviationStatuses: (apiData.deviationStatuses || []).map(s => ({
                  status_id: s.statusID,
                  status_name: s.statusName,
                  activated: s.sponsorSelection || s.cmoSelection || false,
                })),
                inProcessTestStatuses: (apiData.inProcessStatuses || []).map(s => ({
                  status_id: s.statusID,
                  status_name: s.statusName,
                  activated: s.sponsorSelection || s.cmoSelection || false,
                })),
                productReleaseTestStatuses: (apiData.projectReleaseStatuses || []).map(s => ({
                  status_id: s.statusID,
                  status_name: s.statusName,
                  activated: s.sponsorSelection || s.cmoSelection || false,
                })),
              },
              // Document Deliverables - API returns fileFormatID, map to fileFormatTypeID for UI
              documentDeliverables: (apiData.documentDetailsList || []).map(doc => ({
                documentTypeID: doc.documentTypeID || 0,
                fileFormatTypeID: doc.fileFormatID || doc.fileFormatTypeID || 0, // API uses fileFormatID
                orgResponsibleID: doc.uploadOrgCollaboratorTypeID || 0,
                documentCategoryID: doc.documentCategoryID || null,
                documentGroupID: doc.documentGroupID || null,
              })),
              campaignID: {
                collabrixCampaignID: apiData.campaignDetails?.collabrixCampaignID || null,
                campaignID: apiData.campaignDetails?.campaignID || "",
                poNumber: apiData.campaignDetails?.poNumber || "",
                productQuantity: apiData.campaignDetails?.productQuantityRequired || "",
                uom: apiData.campaignDetails?.Uom || "KG",
                defaultView: apiData.campaignDetails?.viewType || "System",
                campaignStartDate: apiData.campaignDetails?.campaignStartDate || null,
                campaignEndDate: apiData.campaignDetails?.campaignEndDate || null,
                manufacturingFacility: apiData.campaignDetails?.manufacturingFacility || "",
                dataGranularityID: apiData.campaignDetails?.dataGranularityID || null,
                numberOfStages: apiData.campaignDetails?.numberOfStages?.toString() || "",
                campaignStatusID: apiData.campaignDetails?.campaignStatusID || null,
                campaignStatusText: apiData.campaignDetails?.campaignStatusText || null,
                campaignStageDetails: (apiData.campaignStageDetails || []).map(stage => ({
                  stageNumber: stage.stageNumber,
                  sponsorStageID: stage.sponsorStageID,
                  cmoStageID: stage.cmoStageID,
                  isActive: stage.isActive,
                  collabrixCampaignStageID: stage.collabrixCampaignStageID,
                })),
                campaignBOMDetails: (apiData.campaignBOMDetails || []).map(bom => ({
                  cmoMaterialCode: bom.cmoMaterialCode,
                  cmoMaterialDescription: bom.cmoMaterialDescription,
                  cmoStageIDs: bom.cmoStageIDs,
                  cmoUOM: bom.cmoUOM,
                  supplierName: bom.supplierName,
                  countryOfOrigin: bom.countryOfOrigin,
                  isActive: bom.isActive,
                  sponsorMaterialCode: bom.sponsorMaterialCode,
                  sponsorMaterialDescription: bom.sponsorMaterialDescription,
                  sponsorUOM: bom.sponsorUOM,
                  materialProvidedBySponsor: bom.materialProvidedBySponsor,
                  collabrixBomID: bom.collabrixBomID,
                })),
                campaignTabConfiguration: (apiData.campaignTabConfiguration || []).map(tab => ({
                  viewTab: tab.viewTab,
                  tabName: tab.tabName,
                  newTabName: tab.newTabName,
                  isActive: tab.isActive,
                  markForDelete: tab.markForDelete,
                  collabrixTabConfigID: tab.collabrixTabConfigID,
                })),
                campaignColumnConfiguration: (apiData.campaignColumnConfiguration || []).map(col => ({
                  columnName: col.columnName,
                  newColumnName: col.newColumnName,
                  isOverallViewSelected: col.isOverallViewSelected,
                  isSystemViewSelected: col.isSystemViewSelected,
                  columnType: col.columnType,
                  markForDelete: col.markForDelete,
                  collabrixTabConfigID: col.collabrixTabConfigID,
                  collabrixColumnConfigID: col.collabrixColumnConfigID,
                })),
              },
            };
            
            // Store in sessionStorage
            sessionStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(mappedData));
            setFormData(mappedData);
            console.log("✅ Project details loaded and mapped:", mappedData);
          } else {
            console.warn("⚠️ API response missing data:", response);
          }
        } catch (error) {
          console.error("❌ Error fetching project details:", error);
        } finally {
          setLoadingProjectData(false);
        }
      }
    };
    
    fetchProjectDetails();
  }, [isEditMode, isEditCampaign, projectData?.collabrixProjectID, location?.state?.collabrixProjectID]);

  // For edit mode, always go to Review page (step 4) which shows all sections
  // The ReviewAndAddProject component will display all sections in read-only mode
  // Users can then click "Edit" on individual sections to edit them
  useEffect(() => {
    try {
      // If in edit mode, always ensure we're on the Review page (step 4)
      // This ensures all sections are visible when editing from dashboard
      if (isEditMode || isEditCampaign) {
        // Force set to step 4 and save to sessionStorage
        setActiveStep(4);
        sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, "4");
        return;
      }

      // For non-edit mode, check if there's already a saved step in sessionStorage
      const savedStep = sessionStorage.getItem(STORAGE_KEYS.CURRENT_STEP);
      
      // Only proceed with step determination if there's no saved step
      // This prevents overwriting the user's current position on page refresh
      if (savedStep !== null) {
        return; // Exit early if step is already saved
      }

      // For new projects, start at step 0
      // (This logic is only for non-edit mode)
    } catch (error) {
      console.error("Error handling project edit step determination:", error);
    }
  }, [isEditMode, isEditCampaign]);

  // Additional effect to ensure activeStep stays at 4 in edit mode
  useEffect(() => {
    if ((isEditMode || isEditCampaign) && activeStep !== 4) {
      setActiveStep(4);
      sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, "4");
    }
  }, [isEditMode, isEditCampaign, activeStep]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      flush();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      flush();
    };
  }, [flush]);

  const cleanStepData = useCallback((stepName, stepData) => {
    if (!stepData || typeof stepData !== "object") {
      return stepData;
    }

    const cleaned = {};

    if (stepName === "productDetails") {
      const validFields = [
        "selectedProduct",
        "projectID",
        "cmoSite",
        "deliverableType",
        "projectStartDate",
        "projectEndDate",
        "productModality",
        "manufacturingFacilityType",
        "productTypeName",
        "manufacturingSupplyType",
        // IDs and metadata needed by later steppers / APIs
        "collabrixProductID",
        "collabrixProjectID",
        "cdmoOrgID",
        "sponsorOrgID",
        "deliverableTypeIDs",
      ];
      validFields.forEach((field) => {
        if (stepData.hasOwnProperty(field)) {
          if (field === "deliverableType") {
            const value = stepData[field];
            cleaned[field] = value 
              ? (Array.isArray(value) ? value : [value])
              : [];
          } else {
            cleaned[field] = stepData[field];
          }
        }
      });
    } else if (stepName === "fgStatuses") {
      const validFields = [
        "projectStatuses",
        "poStatuses",
        "campaignStatuses",
        "batchStatuses",
      ];
      validFields.forEach((field) => {
        if (stepData.hasOwnProperty(field)) {
          cleaned[field] = stepData[field];
        }
      });
    } else if (stepName === "documentDeliverables") {
      return Array.isArray(stepData) ? stepData : [];
    } else if (stepName === "campaignID") {
      const validFields = [
        "campaignID",
        "poNumber",
        "productQuantity",
        "numberOfStages",
        "uom",
        "defaultView",
        "dataGranularity",
        "campaignStartDate",
        "campaignEndDate",
        "manufacturingFacility",
        "stageMapping",
        "materialsNotProvidedByBMS",
        "materialsProvidedByBMS",
        "tabConfiguration",
        "columnConfigurations",
      ];
      validFields.forEach((field) => {
        if (stepData.hasOwnProperty(field)) {
          cleaned[field] = stepData[field];
        }
      });
      delete cleaned.newTabName;
    } else {
      return stepData;
    }

    return cleaned;
  }, []);

  const updateStepData = useCallback(
    (stepName, stepData) => {
      try {
        if (!isMountedRef.current) return;

        const cleanedStepData = cleanStepData(stepName, stepData);

        setFormData((prevData) => {
          if (prevData[stepName] === cleanedStepData) {
            return prevData;
          }

          if (
            typeof prevData[stepName] === "object" &&
            typeof cleanedStepData === "object" &&
            prevData[stepName] !== null &&
            cleanedStepData !== null
          ) {
            try {
              if (
                JSON.stringify(prevData[stepName]) === JSON.stringify(cleanedStepData)
              ) {
                return prevData;
              }
            } catch (e) {
            }
          }

          const validStepNames = [
            "productDetails",
            "fgStatuses",
            "documentDeliverables",
            "campaignID",
          ];

          if (!validStepNames.includes(stepName)) {
            console.warn(`Invalid step name: ${stepName}. Only valid step names will be stored.`);
            return prevData;
          }

          const updatedData = {
            ...prevData,
            [stepName]: cleanedStepData,
          };

          debouncedSetItem(updatedData);

          return updatedData;
        });
      } catch (error) {
        console.error(`Error updating step data for ${stepName}:`, error);
      }
    },
    [debouncedSetItem, cleanStepData]
  );

  const markStepCompleted = useCallback((stepIndex) => {
    try {
      const completedSteps = JSON.parse(
        sessionStorage.getItem(STORAGE_KEYS.COMPLETED_STEPS) || "[]"
      );
      if (!completedSteps.includes(stepIndex)) {
        completedSteps.push(stepIndex);
        sessionStorage.setItem(
          STORAGE_KEYS.COMPLETED_STEPS,
          JSON.stringify(completedSteps)
        );
      }
    } catch (error) {
      console.error("Error marking step as completed:", error);
    }
  }, []);

  const markApiCallSuccessful = useCallback(
    (shouldNavigate = true, nextStepIndex, stepName = null) => {
      markStepCompleted(activeStep);

      const displayStepName = stepName || steps[activeStep];

      setSnackbar({
        open: true,
        message: `${displayStepName} saved successfully`,
        severity: "success",
      });

      if (shouldNavigate) {
        const nextStep =
          typeof nextStepIndex === "number" ? nextStepIndex : activeStep + 1;
        sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, nextStep.toString());
        setActiveStep(nextStep);
      }
    },
    [activeStep, markStepCompleted]
  );

  const handleNext = useCallback(
    (stepIndex) => {
      try {
        let nextStep;
        if (typeof stepIndex === "number") {
          nextStep = stepIndex;
        } else {
          nextStep = activeStep + 1;
        }

        markStepCompleted(activeStep);

        sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, nextStep.toString());

        flush();

        if (isMountedRef.current) {
          setActiveStep(nextStep);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (error) {
        console.error("Error in handleNext:", error);
        if (isMountedRef.current) {
          setSnackbar({
            open: true,
            message: "An error occurred while proceeding to next step",
            severity: "error",
          });
        }
      }
    },
    [activeStep, markStepCompleted, flush]
  );

  const handleBack = useCallback(() => {
    try {
      if (activeStep === undefined || activeStep <= 0) {
        throw new Error("Already at first step");
      }

      setActiveStep((prevActiveStep) => {
        const newStep = Math.max(0, prevActiveStep - 1);
        sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, newStep.toString());
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return newStep;
      });
    } catch (error) {
      console.error("Error in handleBack:", error);
      setSnackbar({
        open: true,
        message: "Cannot go back from first step",
        severity: "error",
      });
    }
  }, [activeStep]);

  const handleGoToStep = useCallback((stepIndex) => {
    try {
      if (stepIndex < 0 || stepIndex >= steps.length) {
        throw new Error("Invalid step index");
      }
      
      flush();
      
      sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, stepIndex.toString());
      
      if (isMountedRef.current) {
        setActiveStep(stepIndex);
      }
    } catch (error) {
      console.error("Error in handleGoToStep:", error);
      setSnackbar({
        open: true,
        message: "An error occurred while navigating to step",
        severity: "error",
      });
    }
  }, [steps.length, flush]);

  const handleSubmit = useCallback(() => {
    try {
      flush();

      markStepCompleted(activeStep);

      setSnackbar({
        open: true,
        message: "Project configuration submitted successfully",
        severity: "success",
      });

      setTimeout(() => {
        sessionStorage.removeItem(STORAGE_KEYS.FORM_DATA);
        sessionStorage.removeItem(STORAGE_KEYS.CURRENT_STEP);
        sessionStorage.removeItem(STORAGE_KEYS.COMPLETED_STEPS);

        history.push(
          `/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/manufacturingcollaboration`
        );
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: error.message || "An error occurred during submission",
        severity: "error",
      });
    }
  }, [activeStep, history, flush, markStepCompleted]);

  const handleGoBack = useCallback(() => {
    flush();

    sessionStorage.removeItem(STORAGE_KEYS.FORM_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.CURRENT_STEP);
    sessionStorage.removeItem(STORAGE_KEYS.COMPLETED_STEPS);

    history.push(
      `/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/manufacturingcollaboration`
    );
  }, [history, flush]);

  const handleCancelClick = useCallback(() => {
    if (activeStep === 2) {
      setCancelWarningDialogOpen(true);
    } else {
      handleGoBack();
    }
  }, [activeStep, handleGoBack]);

  const handleConfirmCancel = useCallback(() => {
    setCancelWarningDialogOpen(false);
    handleGoBack();
  }, [handleGoBack]);

  const handleCloseCancelWarning = useCallback(() => {
    setCancelWarningDialogOpen(false);
  }, []);

  const handleCloseSnackbar = useCallback((event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const getStepContent = useCallback(
    (step) => {
      if (step === undefined || step === null) {
        console.error("Invalid step value");
        return null;
      }

      const commonProps = {
        formData: formData || {},
        updateStepData,
        classes,
        handleNext,
        handleBack,
        handleSubmit,
        handleGoToStep,
        activeStep,
        stepsLength: steps.length,
        markApiCallSuccessful,
        readOnly: false,
        onValidationChange: () => {},
        onValidationError: () => {},
        skipReplicateDialog,
        setSkipReplicateDialog,
        isProductDetailsEditMode: false,
        materialDetails: formData?.materialDetails,
      };

      try {
        switch (step) {
          case 0:
            return <ProductDetails {...commonProps} />;
          case 1:
            return <StatusSelection {...commonProps} fetchFGStatusesAPI={fetchFGStatusesAPI} />;
          case 2:
            return <DocumentDeliverables {...commonProps} fetchFGStatusesAPI={fetchFGStatusesAPI} />;
          case 3:
            return <AddCampaignID {...commonProps} />;
          case 4:
            return <ReviewAndAddProject {...commonProps} updateStepData={updateStepData} markApiCallSuccessful={markApiCallSuccessful} isEditMode={isEditMode} isEditCampaign={isEditCampaign} />;
          default:
            return "Unknown step";
        }
      } catch (error) {
        console.error("Error rendering step content:", error);
        return (
          <div>
            <Typography color="error">
              An error occurred while loading this step. Please try again.
            </Typography>
            <Button
              onClick={() => window.location.reload()}
              variant="outlined"
              color="primary"
            >
              Reload
            </Button>
          </div>
        );
      }
    },
    [
      formData,
      updateStepData,
      classes,
      handleNext,
      handleBack,
      handleSubmit,
      activeStep,
      markApiCallSuccessful,
    ]
  );

  const stepContent = useMemo(() => {
    return getStepContent(activeStep);
  }, [activeStep, getStepContent]);

  if (!isInitialized) {
    return (
      <Box
        p={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className={classes.root} style={{ backgroundColor: "#fff" }}>
      <Paper className={classes.root} elevation={0} style={{ backgroundColor: "#fff", width: "100%" }}>
        <Box className={classes.stepperHeader} style={{ backgroundColor: "#F5F9FF", width: "100%" }}>
          <Typography className={classes.heading} variant="h5">
            {isEditMode ? "Edit Project" : "Add New Project"}
          </Typography>
          <Typography className={classes.subheading}>
            {isEditMode 
              ? "Please review and update the project details and configurations in the fields below."
              : "Please add the project details and configurations in the fields below."}
          </Typography>
          <Box
            className="close-page-action"
            onClick={handleCancelClick}
            style={{
              position: "absolute",
              top: "32px",
              right: "32px",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              gap: "8px",
            }}
          >
            <Typography>Cancel</Typography>
            <CloseIcon />
          </Box>
        </Box>
        {/* Hide stepper in edit mode - user only sees review page directly */}
        {!isEditMode && (
          <Box className={classes.stepperContainer}>
            <Stepper activeStep={activeStep} className={classes.stepper}>
              {steps.map((label, index) => {
                const stepProps = {};
                const labelProps = {};

                return (
                  <Step key={label} {...stepProps}>
                    <StepLabel
                      style={{ whiteSpace: "nowrap" }}
                      className={classes.stepLabel}
                      {...labelProps}
                      StepIconProps={{
                        completed:
                          index < activeStep && (
                            <CheckCircleIcon className={classes.stepCompleted} />
                          ),
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        )}
        <Box className={classes.contentWrapper}>
          <Box className={`${classes.stepContent} ${classes.twoColFormsGrid}`}>{stepContent}</Box>
        </Box>
        <Snackbar
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          key={snackbar.message}
          style={{ zIndex: 9999 }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbar.severity}
            onClose={handleCloseSnackbar}
            action={
              <IconButton
                size="small"
                color="inherit"
                onClick={handleCloseSnackbar}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>

        <Dialog
          open={cancelWarningDialogOpen}
          onClose={handleCloseCancelWarning}
          aria-labelledby="cancel-warning-dialog-title"
          aria-describedby="cancel-warning-dialog-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "8px",
              padding: "8px",
            },
          }}
        >
          <DialogTitle 
            id="cancel-warning-dialog-title"
            style={{
              padding: "24px 24px 16px 24px",
              fontSize: "20px",
              fontWeight: 600,
              color: "#d32f2f",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Confirm Cancellation
          </DialogTitle>
          <DialogContent style={{ padding: "24px" }}>
            <DialogContentText 
              id="cancel-warning-dialog-description"
              style={{
                fontSize: "15px",
                lineHeight: "1.6",
                color: "#424242",
                marginBottom: "8px",
              }}
            >
              Exiting now will permanently delete all unsaved project details and document information.
            </DialogContentText>
            <DialogContentText 
              style={{
                fontSize: "14px",
                lineHeight: "1.5",
                color: "#757575",
                marginTop: "12px",
                fontStyle: "italic",
              }}
            >
              All data must be re-entered if you proceed.
            </DialogContentText>
            <Box
              style={{
                marginTop: "20px",
                padding: "12px",
                backgroundColor: "#fff3cd",
                borderRadius: "4px",
                borderLeft: "4px solid #ffc107",
              }}
            >
              <Typography
                style={{
                  fontSize: "13px",
                  color: "#856404",
                  fontWeight: 500,
                }}
              >
                ⚠️ This action cannot be undone.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #e0e0e0",
              justifyContent: "flex-end",
              gap: "12px",
            }}
          >
            <Button
              onClick={handleCloseCancelWarning}
              variant="outlined"
              style={{
                textTransform: "none",
                padding: "8px 24px",
                borderRadius: "4px",
                borderColor: "#1976d2",
                color: "#1976d2",
                fontWeight: 500,
                minWidth: "120px",
              }}
            >
              Stay on Page
            </Button>
            <Button
              onClick={handleConfirmCancel}
              variant="contained"
              style={{
                textTransform: "none",
                padding: "8px 24px",
                borderRadius: "4px",
                backgroundColor: "#d32f2f",
                color: "#fff",
                fontWeight: 500,
                minWidth: "120px",
                boxShadow: "0 2px 4px rgba(211, 47, 47, 0.3)",
              }}
            >
              Cancel & Exit
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </div>
  );
};

export default ProductDevelopmentStepper;

